import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Box, IconButton } from "@mui/material";
import { motion, AnimatePresence, useScroll } from "framer-motion";
import { Menu, X, ChevronDown, Mail } from "lucide-react";
import useScrollToSection from "../../hooks/useScrollToSection";
import useLang from "../../hooks/useLang";
import { useNavigate, useLocation } from "react-router-dom";
import KanonicsLogo from "../ui/KanonicsLogo";
import { useTranslation } from "react-i18next";

const ease = [0.16, 0.77, 0.47, 0.97] as const;

/*Plain nav item */
const NavItem: React.FC<{ label: string; onClick: () => void }> = ({
  label,
  onClick,
}) => {
  const [hovered, setHovered] = useState(false);
  return (
    <Box
      sx={{ position: "relative", px: "8px", py: "20px", cursor: "pointer" }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onClick}
    >
      <button
        type="button"
        style={{
          background: "none",
          border: "none",
          padding: 0,
          paddingBottom: "3px",
          position: "relative",
          display: "flex",
          alignItems: "center",
          fontSize: "0.875rem",
          fontWeight: 500,
          color: hovered ? "rgba(29,47,65,1)" : "rgba(29,47,65,0.7)",
          fontFamily: '"Calibri", sans-serif',
          cursor: "pointer",
          transition: "color 0.2s",
          pointerEvents: "none",
        }}
      >
        {label}
        <motion.span
          animate={{ scaleX: hovered ? 1 : 0 }}
          transition={{ duration: 0.3, ease }}
          style={{
            position: "absolute",
            bottom: "-1px",
            left: 0,
            right: 0,
            height: "1px",
            backgroundColor: "#1D2F41",
            transformOrigin: "center",
            display: "block",
          }}
        />
      </button>
    </Box>
  );
};

/*  Dropdown item */
interface DropdownItem {
  label: string;
  action: () => void;
}

/*  Tree dropdown */
const TreeDropdown = React.forwardRef<
  HTMLDivElement,
  {
    items: DropdownItem[];
    onEnter: () => void;
    onLeave: () => void;
    top: number;
    left: number;
    navRight: number;
    spineGap: number;
  }
>(({ items, onEnter, onLeave, top, left, navRight, spineGap }, ref) => {
  const BRANCH_H = 56;
  const SPINE_H = items.length * BRANCH_H;
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);

  return (
    <motion.div
      ref={ref}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.12 }}
      style={{
        position: "fixed",
        top: `${top}px`,
        left: `${left}px`,
        right: `${window.innerWidth - navRight}px`,
        zIndex: 9998,
        paddingTop: "14px",
        paddingLeft: "20px",
        paddingRight: "30px",
        background: "rgba(255,255,255,0.3)",
        backdropFilter: "blur(24px)",
        WebkitBackdropFilter: "blur(24px)",
        border: "1px solid rgba(255,255,255,0.5)",
        boxShadow: "0 8px 32px rgba(29,47,65,0.08)",
      }}
    >
      <motion.div
        initial={{ scaleY: 0 }}
        animate={{ scaleY: 1 }}
        transition={{ duration: 0.22, ease }}
        style={{
          position: "absolute",
          left: "20px",
          top: `-${spineGap}px`,
          height: `${SPINE_H - BRANCH_H / 2 + 14 + spineGap}px`,
          width: "1px",
          background: "rgba(29,47,65,0.15)",
          transformOrigin: "top",
        }}
      />

      <motion.div
        animate={{
          height:
            hoveredIdx !== null
              ? spineGap + 14 + hoveredIdx * BRANCH_H + BRANCH_H / 2
              : 0,
        }}
        transition={{ duration: 0.22, ease }}
        style={{
          position: "absolute",
          left: "20px",
          top: `-${spineGap}px`,
          width: "1px",
          background: "rgba(29,47,65,0.55)",
        }}
      />

      {items.map((item, i) => (
        <Box
          key={item.label}
          onClick={item.action}
          sx={{
            display: "flex",
            alignItems: "center",
            height: `${BRANCH_H}px`,
            cursor: "pointer",
          }}
          onMouseEnter={() => setHoveredIdx(i)}
          onMouseLeave={() => setHoveredIdx(null)}
        >
          {/* Horizontal branch */}
          <motion.div
            initial={{ scaleX: 0 }}
            animate={{
              scaleX: 1,
              background:
                hoveredIdx === i
                  ? "rgba(29,47,65,0.55)"
                  : "rgba(29,47,65,0.15)",
            }}
            transition={{
              scaleX: { duration: 0.16, ease, delay: 0.08 + i * 0.05 },
              background: { duration: 0.18 },
            }}
            style={{
              width: "24px",
              height: "1px",
              transformOrigin: "left",
              flexShrink: 0,
            }}
          />

          {/* Label */}
          <motion.div
            initial={{ opacity: 0, x: -6 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.15, delay: 0.08 + i * 0.05 + 0.2 }}
          >
            <motion.div
              animate={{ opacity: hoveredIdx === i ? 1 : 0.55 }}
              transition={{ duration: 0.16 }}
              style={{
                paddingLeft: "14px",
                paddingRight: "8px",
                fontSize: "0.875rem",
                fontWeight: 400,
                color: "#1D2F41",
                fontFamily: '"Calibri", sans-serif',
                whiteSpace: "nowrap",
              }}
            >
              {item.label}
            </motion.div>
          </motion.div>
        </Box>
      ))}
    </motion.div>
  );
});
TreeDropdown.displayName = "TreeDropdown";

/* Services nav item with dropdown  */
const ServicesNavItem: React.FC<{
  label: string;
  items: DropdownItem[];
  scrolled: boolean;
}> = ({ label, items, scrolled }) => {
  const [open, setOpen] = useState(false);
  const [openKey, setOpenKey] = useState(0);
  const [dropTop, setDropTop] = useState(0);
  const [dropLeft, setDropLeft] = useState(0);
  const [navRight, setNavRight] = useState(0);
  const [spineGap, setSpineGap] = useState(0);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const measureRef = useRef<() => void>();
  measureRef.current = () => {
    const w = wrapperRef.current;
    if (!w) return;
    const nav = w.closest("nav");
    if (!nav) return;
    const button = w.querySelector("button");
    if (!button) return;
    const wr = w.getBoundingClientRect();
    const br = button.getBoundingClientRect();
    const nr = nav.getBoundingClientRect();
    setDropTop(nr.bottom);
    // spine is at left:20px inside the dropdown, plus 1px border = 21px from outer edge
    setDropLeft(br.left + br.width / 2 - 21);
    setNavRight(nr.right);
    setSpineGap(nr.bottom - wr.bottom + 20);
  };
  const measure = () => measureRef.current?.();

  const clearClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const handleEnter = () => {
    clearClose();
    if (!open) {
      measure();
      setOpenKey((k) => k + 1);
    }
    setOpen(true);
  };
  const handleLeave = () => {
    if (!closeTimer.current)
      closeTimer.current = setTimeout(() => setOpen(false), 150);
  };

  // Re-measure on resize and scroll
  useEffect(() => {
    if (!open) return;
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [open]);

  // When scrolled state changes while dropdown is open, continuously
  // re-measure for the duration of the navbar's 0.4s transition
  useEffect(() => {
    if (!open) return;
    let raf: number;
    const start = performance.now();
    const DURATION = 500; // slightly longer than the 0.4s transition
    const tick = () => {
      measure();
      if (performance.now() - start < DURATION)
        raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [open, scrolled]);

  useEffect(() => {
    if (!open) return;
    const onMove = (e: MouseEvent) => {
      const w = wrapperRef.current;
      const d = dropdownRef.current;
      if (!w || !d) return;
      const { clientX: x, clientY: y } = e;
      const wr = w.getBoundingClientRect();
      const dr = d.getBoundingClientRect();
      const inWrapper =
        x >= wr.left && x <= wr.right && y >= wr.top && y <= wr.bottom;
      const inDropdown =
        x >= dr.left && x <= dr.right && y >= dr.top && y <= dr.bottom;
      const inCorridor =
        y >= wr.bottom &&
        y <= dr.top &&
        x >= Math.min(wr.left, dr.left) - 12 &&
        x <= Math.max(wr.right, dr.right) + 12;
      if (inWrapper || inDropdown || inCorridor) {
        clearClose();
      } else if (!closeTimer.current) {
        closeTimer.current = setTimeout(() => setOpen(false), 150);
      }
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, [open]);

  return (
    <>
      <Box
        ref={wrapperRef}
        sx={{ position: "relative", px: "8px", py: "20px", cursor: "pointer" }}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
      >
        {/* Trigger */}
        <button
          type="button"
          style={{
            background: "none",
            border: "none",
            padding: 0,
            paddingBottom: "3px",
            position: "relative",
            display: "flex",
            alignItems: "center",
            gap: "4px",
            fontSize: "0.875rem",
            fontWeight: 500,
            color: open ? "rgba(29,47,65,1)" : "rgba(29,47,65,0.7)",
            fontFamily: '"Calibri", sans-serif',
            cursor: "pointer",
            transition: "color 0.2s",
          }}
        >
          {label}
          <motion.span
            animate={{ rotate: open ? 180 : 0 }}
            transition={{ duration: 0.25, ease }}
            style={{
              display: "inline-flex",
              alignItems: "center",
              marginTop: "1px",
            }}
          >
            <ChevronDown size={13} strokeWidth={2} />
          </motion.span>
          <motion.span
            animate={{ scaleX: open ? 1 : 0 }}
            transition={{ duration: 0.3, ease }}
            style={{
              position: "absolute",
              bottom: "-1px",
              left: 0,
              right: 0,
              height: "1px",
              backgroundColor: "#1D2F41",
              transformOrigin: "center",
              display: "block",
            }}
          />
        </button>
      </Box>

      {/* Portal lives outside Box so ReactPortal is never a Box child */}
      {open &&
        createPortal(
          <TreeDropdown
            key={openKey}
            ref={dropdownRef}
            items={items}
            onEnter={handleEnter}
            onLeave={handleLeave}
            top={dropTop}
            left={dropLeft}
            navRight={navRight}
            spineGap={spineGap}
          />,
          document.body,
        )}
    </>
  );
};

/* Navbar */
const Navbar: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { scrollY } = useScroll();
  const scrollToSection = useScrollToSection();
  const navigate = useNavigate();
  const location = useLocation();
  const lang = useLang();
  const { t } = useTranslation();

  useEffect(() => {
    const unsub = scrollY.on("change", (v) => setScrolled(v > 40));
    return unsub;
  }, [scrollY]);

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMenuOpen]);

  useEffect(() => {
    if (location.state?.sectionId) {
      const el = document.getElementById(location.state.sectionId);
      if (el) {
        const top = el.getBoundingClientRect().top + window.scrollY - 100;
        window.scrollTo({ top, behavior: "smooth" });
      }
    }
    if (location.state?.capabilityIndex !== undefined) {
      window.dispatchEvent(
        new CustomEvent("capability:open", {
          detail: location.state.capabilityIndex,
        }),
      );
    }
  }, [location]);

  const handleLogoClick = () => {
    navigate(`/${lang}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
    setIsMenuOpen(false);
  };

  const handleNavClick = (sectionId: string) => {
    const homePath = `/${lang}`;
    if (location.pathname !== homePath) {
      navigate(homePath, { state: { sectionId } });
    } else {
      scrollToSection(sectionId);
    }
    setIsMenuOpen(false);
  };

  const handleCapabilityClick = (capabilityIndex: number) => {
    const homePath = `/${lang}`;
    window.dispatchEvent(
      new CustomEvent("capability:open", { detail: capabilityIndex }),
    );
    if (location.pathname !== homePath) {
      navigate(homePath, {
        state: { sectionId: "solutions", capabilityIndex },
      });
    } else {
      scrollToSection("solutions");
    }
    setIsMenuOpen(false);
  };

  const handleLangSwitch = (targetLang: string) => {
    const rest = location.pathname.replace(/^\/(en|de)/, "") || "";
    navigate(`/${targetLang}${rest}`, { replace: true });
    setIsMenuOpen(false);
  };

  const dropdown: DropdownItem[] = [
    {
      label: t("nav.servicesDropdown.digitalTransformation"),
      action: () => handleCapabilityClick(0),
    },
    {
      label: t("nav.servicesDropdown.dltAI"),
      action: () => handleCapabilityClick(1),
    },
    {
      label: t("nav.servicesDropdown.platformEngineering"),
      action: () => handleCapabilityClick(2),
    },
    {
      label: t("nav.servicesDropdown.dataCloud"),
      action: () => handleCapabilityClick(3),
    },
  ];

  const navAnimate = {
    height: scrolled ? 72 : 80,
    backgroundColor: scrolled ? "rgba(255,255,255,0.3)" : "rgba(255,255,255,0)",
    boxShadow: scrolled
      ? "0 8px 32px rgba(29,47,65,0.08)"
      : "0 0px 0px rgba(29,47,65,0)",
    borderRadius: "0px",
    marginTop: scrolled ? "20px" : "0px",
    maxWidth: scrolled ? "1200px" : "1280px",
    width: scrolled ? "calc(100% - 40px)" : "100%",
  };

  return (
    <>
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 9997,
          display: "flex",
          justifyContent: "center",
          pointerEvents: "none",
        }}
      >
        <Box
          component={motion.nav}
          initial={{
            height: 80,
            backgroundColor: "rgba(255,255,255,0)",
            boxShadow: "0 0px 0px rgba(29,47,65,0)",
            borderRadius: "0px",
            marginTop: "0px",
            maxWidth: "1280px",
            width: "100%",
          }}
          animate={navAnimate}
          transition={{ duration: 0.4, ease }}
          sx={{
            px: { xs: 3, md: 4 },
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pointerEvents: "auto",
            backdropFilter: scrolled ? "blur(24px)" : "none",
            WebkitBackdropFilter: scrolled ? "blur(24px)" : "none",
            border: scrolled
              ? "1px solid rgba(255,255,255,0.5)"
              : "1px solid rgba(255,255,255,0)",
            transition: "backdrop-filter 0.4s, border 0.4s",
          }}
        >
          {/* Logo */}
          <Box
            onClick={handleLogoClick}
            sx={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            <KanonicsLogo />
          </Box>

          {/* Desktop nav */}
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              alignItems: "center",
              gap: 5,
            }}
          >
            <>
              <NavItem
                label={t("nav.about")}
                onClick={() => handleNavClick("about")}
              />
              <NavItem
                label={t("nav.projects")}
                onClick={() => handleNavClick("projects")}
              />
              <ServicesNavItem
                label={t("nav.services")}
                items={dropdown}
                scrolled={scrolled}
              />
            </>

            {/* Language switcher */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {(["en", "de"] as const).map((l, i) => (
                <React.Fragment key={l}>
                  {i > 0 && (
                    <Box
                      sx={{
                        width: "1px",
                        height: "12px",
                        bgcolor: "rgba(29,47,65,0.25)",
                      }}
                    />
                  )}
                  <Box
                    component="button"
                    onClick={() => handleLangSwitch(l)}
                    sx={{
                      background: "none",
                      border: "none",
                      padding: 0,
                      pb: "3px",
                      px: "6px",
                      fontSize: "0.8rem",
                      fontWeight: lang === l ? 700 : 400,
                      color:
                        lang === l ? "rgba(29,47,65,1)" : "rgba(29,47,65,0.45)",
                      fontFamily: '"Calibri", sans-serif',
                      cursor: lang === l ? "default" : "pointer",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      transition: "color 0.2s",
                      "&:hover": {
                        color: lang === l ? undefined : "rgba(29,47,65,0.8)",
                      },
                    }}
                  >
                    {l}
                  </Box>
                </React.Fragment>
              ))}
            </Box>

            <IconButton
              onClick={() => handleNavClick("contact")}
              aria-label={t("nav.contactByEmail")}
              sx={{
                color: "#1D2F41",
                bgcolor: "rgba(29,47,65,0.07)",
                borderRadius: "4px",
                p: 1,
                "&:hover": { bgcolor: "rgba(29,47,65,0.13)" },
              }}
            >
              <Mail size={18} strokeWidth={1.75} />
            </IconButton>
          </Box>

          {/* Mobile toggle */}
          <IconButton
            sx={{ display: { md: "none" }, color: "#1D2F41" }}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? t("nav.closeMenu") : t("nav.openMenu")}
            aria-expanded={isMenuOpen}
            aria-controls="mobile-menu"
          >
            {isMenuOpen ? <X /> : <Menu />}
          </IconButton>
        </Box>
      </Box>

      {/* Mobile menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <Box
            id="mobile-menu"
            component={motion.div}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            sx={{
              position: "fixed",
              inset: 0,
              zIndex: 9996,
              bgcolor: "#ffffff",
              pt: scrolled ? "116px" : "96px",
              px: 3,
              display: { md: "none" },
              overflowY: "auto",
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <>
                <MobileRow
                  label={t("nav.about")}
                  onClick={() => handleNavClick("about")}
                />
                <MobileRow
                  label={t("nav.projects")}
                  onClick={() => handleNavClick("projects")}
                />

                {/* Services accordion */}
                <Box>
                  <Box
                    component="button"
                    onClick={() => setMobileServicesOpen((v) => !v)}
                    sx={{
                      width: "100%",
                      background: "none",
                      border: "none",
                      borderBottom: "1px solid rgba(29,47,65,0.1)",
                      pb: 2,
                      mb: 3,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      textAlign: "left",
                      fontSize: "1.5rem",
                      fontWeight: 500,
                      color: "#1D2F41",
                      fontFamily: '"Segoe UI Semibold", "Segoe UI", sans-serif',
                      cursor: "pointer",
                    }}
                  >
                    {t("nav.services")}
                    <motion.div
                      animate={{ rotate: mobileServicesOpen ? 180 : 0 }}
                      transition={{ duration: 0.25 }}
                      style={{ display: "flex" }}
                    >
                      <ChevronDown size={20} />
                    </motion.div>
                  </Box>
                  <AnimatePresence>
                    {mobileServicesOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease }}
                        style={{ overflow: "hidden" }}
                      >
                        <Box
                          sx={{
                            pl: 2,
                            pb: 3,
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          {dropdown.map((item) => (
                            <Box
                              key={item.label}
                              component="button"
                              onClick={item.action}
                              sx={{
                                background: "none",
                                border: "none",
                                borderBottom: "1px solid rgba(29,47,65,0.06)",
                                py: 1.5,
                                textAlign: "left",
                                fontSize: "1rem",
                                fontWeight: 400,
                                color: "rgba(29,47,65,0.65)",
                                fontFamily: '"Calibri", sans-serif',
                                cursor: "pointer",
                              }}
                            >
                              {item.label}
                            </Box>
                          ))}
                        </Box>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Box>

                <MobileRow
                  label={t("nav.contactUs")}
                  onClick={() => handleNavClick("contact")}
                  accent
                />
              </>

              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, pt: 2 }}
              >
                {(["en", "de"] as const).map((l, i) => (
                  <React.Fragment key={l}>
                    {i > 0 && (
                      <Box
                        sx={{
                          width: "1px",
                          height: "16px",
                          bgcolor: "rgba(29,47,65,0.25)",
                        }}
                      />
                    )}
                    <Box
                      component="button"
                      onClick={() => handleLangSwitch(l)}
                      sx={{
                        background: "none",
                        border: "none",
                        padding: "4px 8px",
                        fontSize: "1rem",
                        fontWeight: lang === l ? 700 : 400,
                        color:
                          lang === l
                            ? "rgba(29,47,65,1)"
                            : "rgba(29,47,65,0.4)",
                        fontFamily: '"Calibri", sans-serif',
                        cursor: lang === l ? "default" : "pointer",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                      }}
                    >
                      {l}
                    </Box>
                  </React.Fragment>
                ))}
              </Box>
            </Box>
          </Box>
        )}
      </AnimatePresence>
    </>
  );
};

const MobileRow: React.FC<{
  label: string;
  onClick: () => void;
  accent?: boolean;
}> = ({ label, onClick, accent }) => (
  <Box
    component="button"
    onClick={onClick}
    sx={{
      background: "none",
      border: "none",
      borderBottom: "1px solid rgba(29,47,65,0.1)",
      pb: 2,
      mb: 3,
      textAlign: "left",
      fontSize: "1.5rem",
      fontWeight: 500,
      color: accent ? "#22464E" : "#1D2F41",
      fontFamily: '"Segoe UI Semibold", "Segoe UI", sans-serif',
      cursor: "pointer",
      width: "100%",
    }}
  >
    {label}
  </Box>
);

export default Navbar;
